import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { IconX, IconHeart } from './Icons';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface JournalEntryPanelProps {
  fragrance: {
    fragranceId: string;
    name: string;
    house: string;
    notes?: string[];
    topNotes?: string[];
    middleNotes?: string[];
    baseNotes?: string[];
  };
  userId: string;
  authToken: string;
  existingEntry?: any;       // full entry (for context)
  editLog?: any;             // specific wear log to edit (null = new session)
  presetRating?: string;
  onClose: () => void;
  onSaved: () => void;
}

const TIMES = ['morning', 'afternoon', 'evening', 'night', 'all day long'];
const SEASONS = ['spring', 'summer', 'autumn', 'winter', 'year-round'];
const TIME_CATCHALL = 'all day long';
const SEASON_CATCHALL = 'year-round';
const OWNERSHIP_OPTIONS = [
  { value: 'sample', label: 'I own a sample', sub: null },
  { value: 'bottle', label: 'I own a bottle', sub: null },
  { value: 'would_buy_again', label: 'I would buy it again', sub: null },
  { value: 'sold', label: 'I no longer own it', sub: 'Previously owned, sold, or gifted away' },
];

const JournalEntryPanel: React.FC<JournalEntryPanelProps> = ({
  fragrance,
  userId,
  authToken,
  existingEntry,
  editLog,
  presetRating,
  onClose,
  onSaved,
}) => {
  const isEditing = !!editLog;
  const [notesOpen, setNotesOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [form, setForm] = useState({
    rating: presetRating || 'neutral',
    ownership: [] as string[],
    timeOfDay: [] as string[],
    season: [] as string[],
    topNotesImpression: '',
    heartNotesImpression: '',
    baseNotesImpression: '',
    notes: '',
  });

  useEffect(() => {
    if (editLog) {
      setForm({
        rating: editLog.rating || 'neutral',
        ownership: editLog.ownership || [],
        timeOfDay: editLog.timeOfDay ? editLog.timeOfDay.split(', ') : [],
        season: editLog.season ? editLog.season.split(', ') : [],
        topNotesImpression: editLog.topNotesImpression || '',
        heartNotesImpression: editLog.heartNotesImpression || '',
        baseNotesImpression: editLog.baseNotesImpression || '',
        notes: editLog.notes || '',
      });
    } else if (existingEntry && !editLog) {
      // New session — start blank except for rating hint
      setForm(f => ({ ...f, rating: presetRating || 'neutral' }));
    }
  }, [editLog, existingEntry]);

  const toggle = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];

  const toggleTime = (value: string) => {
    if (value === TIME_CATCHALL) {
      // selecting catch-all clears everything else, or deselects if already selected
      const next = form.timeOfDay.includes(value) ? [] : [value];
      setForm({ ...form, timeOfDay: next });
    } else {
      // selecting a specific time clears the catch-all, then toggles normally
      const without = form.timeOfDay.filter(v => v !== TIME_CATCHALL);
      setForm({ ...form, timeOfDay: toggle(without, value) });
    }
  };

  const toggleSeason = (value: string) => {
    if (value === SEASON_CATCHALL) {
      const next = form.season.includes(value) ? [] : [value];
      setForm({ ...form, season: next });
    } else {
      const without = form.season.filter(v => v !== SEASON_CATCHALL);
      setForm({ ...form, season: toggle(without, value) });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const wearLogPayload = {
        rating: form.rating,
        ownership: form.ownership,
        timeOfDay: form.timeOfDay.length > 0 ? form.timeOfDay.join(', ') : null,
        season: form.season.length > 0 ? form.season.join(', ') : null,
        topNotesImpression: form.topNotesImpression || null,
        heartNotesImpression: form.heartNotesImpression || null,
        baseNotesImpression: form.baseNotesImpression || null,
        notes: form.notes || null,
      };

      await axios.post(`${API_URL}/journal`, {
        action: isEditing ? 'editWearLog' : 'addWearLog',
        userId,
        fragranceId: fragrance.fragranceId,
        fragranceName: fragrance.name,
        fragranceHouse: fragrance.house,
        notes: fragrance.notes || [],
        logId: isEditing ? editLog.logId : undefined,
        wearLog: wearLogPayload,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setSaved(true);
      setTimeout(() => {
        onSaved();
        onClose();
      }, 900);
    } catch (e) {
      console.error('Failed to save journal entry:', e);
      setSaveError(true);
    }
    setSaving(false);
  };

  const ratingOptions = [
    { value: 'love', label: '★ All-time Fave' },
    { value: 'like', label: null }, // rendered specially with heart icon
    { value: 'neutral', label: '~ Neutral' },
    { value: 'dislike', label: '✕ Dislike' },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-charcoal bg-opacity-40 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 bg-cream z-50 rounded-t-2xl max-h-[92vh] overflow-y-auto md:max-w-2xl md:mx-auto">
        <div className="relative px-6 pt-6 pb-24 md:pb-10">

          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="w-12 h-1 bg-cream-dark rounded-full" />
            <button onClick={onClose} className="absolute right-6 top-6 text-stone-light hover:text-charcoal transition-colors">
              <IconX size={18} />
            </button>
          </div>

          <p className="font-serif text-xl text-charcoal mb-1">
            {isEditing ? 'Edit Wear Session' : 'Log a Wear Session'}
          </p>
          <p className="text-stone-light text-sm mb-1">{fragrance.name}</p>
          <p className="text-stone-light text-xs tracking-widests uppercase mb-4">{fragrance.house}</p>

          {/* Collapsible note pyramid */}
          {(fragrance.topNotes?.length || fragrance.middleNotes?.length || fragrance.baseNotes?.length || fragrance.notes?.length) ? (
            <div className="bg-white border border-cream-dark rounded-xl overflow-hidden mb-6">
              <button
                onClick={() => setNotesOpen(o => !o)}
                className="w-full flex justify-between items-center px-4 py-3 text-left"
              >
                <span className="text-xs tracking-widest uppercase text-stone-light">Fragrance notes</span>
                <span className="text-stone-light text-xs transition-transform duration-200" style={{ display: 'inline-block', transform: notesOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
              </button>
              {notesOpen ? (
                <div className="px-4 pb-4 border-t border-cream-dark space-y-3 pt-3">
                  {fragrance.topNotes?.length ? (
                    <div>
                      <p className="text-[8px] tracking-widest uppercase text-stone-light mb-2">Top</p>
                      <div className="flex flex-wrap gap-1.5">
                        {fragrance.topNotes.map(n => (
                          <span key={n} className="text-[10px] px-2.5 py-1 rounded-full border" style={{ borderColor: '#89C4D4', color: '#2A5F6A', background: '#EBF7FA' }}>{n}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {fragrance.middleNotes?.length ? (
                    <div>
                      <p className="text-[8px] tracking-widest uppercase text-stone-light mb-2">Heart</p>
                      <div className="flex flex-wrap gap-1.5">
                        {fragrance.middleNotes.map(n => (
                          <span key={n} className="text-[10px] px-2.5 py-1 rounded-full border" style={{ borderColor: '#D4A0A8', color: '#6A2A35', background: '#FAECED' }}>{n}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {fragrance.baseNotes?.length ? (
                    <div>
                      <p className="text-[8px] tracking-widest uppercase text-stone-light mb-2">Base</p>
                      <div className="flex flex-wrap gap-1.5">
                        {fragrance.baseNotes.map(n => (
                          <span key={n} className="text-[10px] px-2.5 py-1 rounded-full border" style={{ borderColor: '#C4A882', color: '#6A4F2A', background: '#FAF0E3' }}>{n}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {!fragrance.topNotes?.length && !fragrance.middleNotes?.length && !fragrance.baseNotes?.length && fragrance.notes?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {fragrance.notes.map(n => (
                        <span key={n} className="text-[10px] px-2.5 py-1 rounded-full border border-cream-dark text-stone">{n}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="px-4 pb-3 text-[10px] text-stone-light leading-relaxed">
                  {[...(fragrance.topNotes || []), ...(fragrance.middleNotes || []), ...(fragrance.baseNotes || []), ...(fragrance.notes || [])].slice(0, 6).join(' · ')}
                </p>
              )}
            </div>
          ) : null}

          {saved ? (
            <div className="text-center py-10">
              <p className="font-serif text-2xl text-charcoal mb-2">Saved.</p>
              <p className="text-stone text-sm">{fragrance.name} updated in your journal.</p>
            </div>
          ) : (
            <>
              {/* Rating */}
              <div className="mb-6">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-3">How do you feel about it?</p>
                <div className="flex gap-2">
                  {ratingOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setForm({ ...form, rating: opt.value })}
                      className={`flex-1 py-3 text-xs tracking-widest uppercase border rounded-xl transition-colors flex items-center justify-center gap-1 ${
                        form.rating === opt.value
                          ? 'border-charcoal bg-white text-charcoal font-medium'
                          : 'border-cream-dark bg-white text-stone-light hover:border-stone'
                      }`}
                    >
                      {opt.value === 'like'
                        ? <><IconHeart size={11} strokeWidth={1.5} /> Like</>
                        : opt.label
                      }
                    </button>
                  ))}
                </div>
              </div>

              {/* Time of day */}
              <div className="mb-6">
                <p className="text-xs tracking-widests uppercase text-stone-light mb-3">Time of day</p>
                <div className="flex gap-2 flex-wrap">
                  {TIMES.map(t => (
                    <button
                      key={t}
                      onClick={() => toggleTime(t)}
                      className={`px-4 py-2 text-xs tracking-widest uppercase border rounded-full transition-colors ${
                        form.timeOfDay.includes(t)
                          ? 'border-charcoal bg-white text-charcoal'
                          : 'border-cream-dark bg-white text-stone-light hover:border-stone'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Season */}
              <div className="mb-6">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Season</p>
                <div className="flex gap-2 flex-wrap">
                  {SEASONS.map(s => (
                    <button
                      key={s}
                      onClick={() => toggleSeason(s)}
                      className={`px-4 py-2 text-xs tracking-widest uppercase border rounded-full transition-colors ${
                        form.season.includes(s)
                          ? 'border-charcoal bg-white text-charcoal'
                          : 'border-cream-dark bg-white text-stone-light hover:border-stone'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ownership */}
              <div className="mb-6">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-1">Ownership · select all that apply</p>
                <p className="text-xs text-stone-light mb-3">Nothing selected means you've tried or smelled it somewhere — that counts too.</p>
                <div className="bg-white border border-cream-dark rounded-xl overflow-hidden">
                  {OWNERSHIP_OPTIONS.map((opt, i) => (
                    <button
                      key={opt.value}
                      onClick={() => setForm({ ...form, ownership: toggle(form.ownership, opt.value) })}
                      className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${
                        i > 0 ? 'border-t border-cream-dark' : ''
                      } ${form.ownership.includes(opt.value) ? 'bg-cream' : 'hover:bg-cream'}`}
                    >
                      <div>
                        <p className="text-sm text-charcoal">{opt.label}</p>
                        {opt.sub && <p className="text-xs text-stone-light mt-0.5">{opt.sub}</p>}
                      </div>
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ml-4 transition-colors ${
                        form.ownership.includes(opt.value)
                          ? 'bg-charcoal border-charcoal'
                          : 'border-cream-dark bg-white'
                      }`}>
                        {form.ownership.includes(opt.value) && (
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Impressions */}
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-2">First impression</p>
                  <textarea
                    value={form.topNotesImpression}
                    onChange={e => setForm({ ...form, topNotesImpression: (e.target as HTMLTextAreaElement).value })}
                    placeholder="Opening notes, what hit you first..."
                    rows={2}
                    className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors text-sm leading-relaxed"
                  />
                </div>
                <div>
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-2">After an hour</p>
                  <textarea
                    value={form.heartNotesImpression}
                    onChange={e => setForm({ ...form, heartNotesImpression: (e.target as HTMLTextAreaElement).value })}
                    placeholder="How did it develop on your skin..."
                    rows={2}
                    className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors text-sm leading-relaxed"
                  />
                </div>
                <div>
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-2">End of day</p>
                  <textarea
                    value={form.baseNotesImpression}
                    onChange={e => setForm({ ...form, baseNotesImpression: (e.target as HTMLTextAreaElement).value })}
                    placeholder="The dry-down, longevity, sillage..."
                    rows={2}
                    className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors text-sm leading-relaxed"
                  />
                </div>
                <div>
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Personal notes</p>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: (e.target as HTMLTextAreaElement).value })}
                    placeholder="Mood, occasion, memories it evokes..."
                    rows={3}
                    className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors text-sm leading-relaxed"
                  />
                </div>
              </div>

              {saveError && (
                <p className="text-red-400 text-xs tracking-widest uppercase text-center mb-3">Something went wrong. Please try again.</p>
              )}
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 border border-cream-dark text-stone py-4 text-xs tracking-widest uppercase rounded-xl hover:border-stone transition-colors">
                  Cancel
                </button>
                <button
                  onClick={() => { setSaveError(false); handleSave(); }}
                  disabled={saving}
                  className="flex-2 w-full bg-charcoal text-cream py-4 text-xs tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : isEditing ? 'Update session' : 'Save wear session'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default JournalEntryPanel;
